import { Button, Card, Heading, Scrim, Text } from "@/components/ui";
import { useModalLayer } from "@/contexts/ModalContext";
import { cn } from "@/lib/cn";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { getDeliveryPickupCode, shouldShowPickupCode } from "@/lib/order-status";
import { TRANSITION, dialogVariants } from "@/lib/motion";
import type { OrderResponse } from "@/types/external";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";

type Props = {
  order: OrderResponse;
};

const MotionScrim = motion.create(Scrim);

function CodeCell({ digit, large = false }: { digit: string; large?: boolean }) {
  return (
    <Card
      as="span"
      variant="tinted"
      tone="primary"
      emphasis={large ? "strong" : "soft"}
      frame="thick"
      padding="none"
      className={cn("flex min-w-0 flex-1 items-center justify-center", large ? "h-24" : "h-20 sm:h-24")}
    >
      <Text as="span" weight="bold" tone="default" tabular size={large ? "4xl" : { base: "3xl", sm: "4xl" }}>
        {digit}
      </Text>
    </Card>
  );
}

/**
 * Prominent 4-digit door code, shown only after the order is dispatched for
 * delivery (or already picked up). Tapping the card opens a large dialog so the
 * customer can show the digits to the rider at the door.
 */
export function DeliveryPickupCodeCard({ order }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const { zIndex, backdropZIndex } = useModalLayer(modalOpen);
  useLockBodyScroll(modalOpen);

  if (!shouldShowPickupCode(order)) return null;

  const code = getDeliveryPickupCode(order);
  if (!code) return null;

  const cells = code.replace(/\s/g, "").toUpperCase().slice(0, 4).split("");
  while (cells.length < 4) cells.push("·");

  return (
    <Dialog.Root open={modalOpen} onOpenChange={setModalOpen} modal>
      <Card as="section" role="region" aria-label="Código de entrega" variant="outline" tone="primary" emphasis="faint" padding="lg">
        <div className="mb-3">
          <Heading level={2} size="section">
            Código de entrega
          </Heading>
          <Text size="sm" tone="muted" className="mt-0.5">
            Informe ao entregador na porta
          </Text>
        </div>

        <Dialog.Trigger asChild>
          <Button
            variant="plain"
            tone="neutral"
            size="auto"
            shape="square"
            fullWidth
            className="items-stretch gap-1.5 sm:gap-2"
            aria-label={`Código de entrega ${cells.join(" ")}`}
          >
            {cells.map((digit, i) => (
              <CodeCell key={`${digit}-${i}`} digit={digit} />
            ))}
          </Button>
        </Dialog.Trigger>
      </Card>

      <AnimatePresence>
        {modalOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay forceMount asChild>
              <MotionScrim
                zIndex={backdropZIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={TRANSITION.fade}
              />
            </Dialog.Overlay>
            <div style={{ zIndex }} className="pointer-events-none fixed inset-0 grid place-items-center p-4">
              <Dialog.Content forceMount asChild aria-modal="true">
                <motion.div
                  className="pointer-events-auto w-full max-w-sm rounded-sheet shadow-overlay"
                  variants={dialogVariants(reduceMotion)}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card padding="2xl" radius="sheet">
                    <Dialog.Title asChild>
                      <Heading level={2} size="section" align="center">
                        Código de entrega
                      </Heading>
                    </Dialog.Title>
                    <Dialog.Description asChild>
                      <Text size="sm" tone="muted" align="center" className="mt-1">
                        Mostre este código ao entregador
                      </Text>
                    </Dialog.Description>

                    <div
                      className="mt-6 flex w-full items-stretch justify-center gap-2"
                      role="group"
                      aria-label={`Código ${cells.join(" ")}`}
                    >
                      {cells.map((digit, i) => (
                        <CodeCell key={`modal-${digit}-${i}`} digit={digit} large />
                      ))}
                    </div>

                    <Dialog.Close asChild>
                      <Button fullWidth className="mt-8">
                        Fechar
                      </Button>
                    </Dialog.Close>
                  </Card>
                </motion.div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

export default DeliveryPickupCodeCard;
